import { Routes } from '@angular/router';
import { anonGuard } from '@taxora-accountant-auth/data-access/guards/anon-guard';
import { authGuard } from '@taxora-accountant-auth/data-access/guards/auth-guard';
import { checkEmailGuard } from '@taxora-accountant-auth/data-access/guards/check-email-guard';
import { AuthState } from '@taxora-accountant-auth/data-access/state/auth-state';
import { AuthenticatedLayout } from '@taxora-accountant-core/feature/authenticated-layout/authenticated-layout';
import { MainLayout } from '@taxora-accountant-core/feature/main-layout/main-layout';
import { APP_ROUTES } from '@taxora-accountant-core/util/constants/app-routes.constant';

export const routes: Routes = [
  { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.DASHBOARD },
  {
    path: '',
    canActivate: [anonGuard],
    component: AuthenticatedLayout,
    children: [
      { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.DASHBOARD },
      {
        path: APP_ROUTES.DASHBOARD,
        loadComponent: () =>
          import('@taxora-accountant-dashboard/feature/dashboard-page/dashboard-page'),
      },
      {
        path: APP_ROUTES.PROFILE,
        loadComponent: () => import('../profile/feature/profile-page/profile-page'),
      },
      {
        path: APP_ROUTES.CLIENTS.ROOT,
        loadComponent: () =>
          import('../clients/feature/clients-page/clients-page').then((m) => m.ClientsPage),
      },
      {
        path: `${APP_ROUTES.CLIENTS.ROOT}/${APP_ROUTES.CLIENTS.DETAILS}`,
        loadComponent: () =>
          import('../clients/feature/client-workspace-page/client-workspace-page').then(
            (m) => m.ClientWorkspacePage,
          ),
      },
      {
        path: APP_ROUTES.INVITATIONS.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            loadComponent: () =>
              import('@taxora-accountant-invitations/feature/invitations-page/invitations-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.ADMINISTRATION.ROOT,
        children: [
          {
            path: APP_ROUTES.ADMINISTRATION.ORGANIZATION.ROOT,
            children: [
              {
                path: '',
                loadComponent: () =>
                  import('@taxora-accountant-administration/feature/organization-details-page/organization-details-page').then(
                    (m) => m.OrganizationDetailsPage,
                  ),
              },
              {
                path: APP_ROUTES.ADMINISTRATION.ORGANIZATION.CREATE,
                loadComponent: () =>
                  import('@taxora-accountant-administration/feature/organization-create-page/organization-create-page').then(
                    (m) => m.OrganizationCreatePage,
                  ),
              },
              {
                path: APP_ROUTES.ADMINISTRATION.ORGANIZATION.EDIT,
                loadComponent: () =>
                  import('@taxora-accountant-administration/feature/organization-edit-page/organization-edit-page').then(
                    (m) => m.OrganizationEditPage,
                  ),
              },
              {
                path: APP_ROUTES.ADMINISTRATION.ORGANIZATION.DETAILS,
                loadComponent: () =>
                  import('@taxora-accountant-administration/feature/organization-details-page/organization-details-page').then(
                    (m) => m.OrganizationDetailsPage,
                  ),
              },
            ],
          },
        ],
      },
    ],
  },
  {
    path: APP_ROUTES.AUTH.ROOT,
    canActivate: [authGuard],
    component: MainLayout,
    children: [
      { path: '', pathMatch: 'full', redirectTo: APP_ROUTES.AUTH.LOGIN },
      {
        path: APP_ROUTES.AUTH.REGISTER.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/create-account-page/create-account-page'),
          },
          {
            path: APP_ROUTES.AUTH.REGISTER.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.createUserEmail,
            },
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/create-account-success-page/create-account-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.ACTIVATE.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: APP_ROUTES.AUTH.ACTIVATE.TOKEN,
          },
          {
            path: APP_ROUTES.AUTH.ACTIVATE.SUCCESS,
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/activate-account-success-page/activate-account-success-page'),
          },
          {
            path: APP_ROUTES.AUTH.ACTIVATE.TOKEN,
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/activate-account-page/activate-account-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.RESEND_ACTIVATION.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/resend-activation-page/resend-activation-page'),
          },
          {
            path: APP_ROUTES.AUTH.RESEND_ACTIVATION.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.resendActivationEmail,
            },
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/resend-activation-success-page/resend-activation-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.LOGIN,
        loadComponent: () => import('@taxora-accountant-auth/feature/login-page/login-page'),
      },
      {
        path: APP_ROUTES.AUTH.FORGOT_PASSWORD.ROOT,
        children: [
          {
            path: '',
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/forgot-password-page/forgot-password-page'),
          },
          {
            path: APP_ROUTES.AUTH.FORGOT_PASSWORD.SUCCESS,
            canActivate: [checkEmailGuard],
            data: {
              emailSelector: AuthState.forgotPasswordEmail,
            },
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/forgot-password-success-page/forgot-password-success-page'),
          },
        ],
      },
      {
        path: APP_ROUTES.AUTH.SET_PASSWORD.ROOT,
        children: [
          {
            path: '',
            pathMatch: 'full',
            redirectTo: APP_ROUTES.AUTH.SET_PASSWORD.TOKEN,
          },
          {
            path: APP_ROUTES.AUTH.SET_PASSWORD.SUCCESS,
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/set-password-success-page/set-password-success-page'),
          },
          {
            path: APP_ROUTES.AUTH.SET_PASSWORD.TOKEN,
            loadComponent: () =>
              import('@taxora-accountant-auth/feature/set-password-page/set-password-page'),
          },
        ],
      },
      { path: '**', redirectTo: APP_ROUTES.AUTH.LOGIN },
    ],
  },
  { path: '**', redirectTo: APP_ROUTES.DASHBOARD },
];
